import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { sh2RegisterApi } from "../../../api/Sh2RegisterApi";

// ── All columns matching SH-2 response ───────────────────────────────────────
const COLUMNS = [
  { key: "serialNo",                        label: "S.No",                                                                      col: "(1)"  },
  { key: "folioNo",                         label: "Folio No.",                                                                  col: "(2)"  },
  { key: "nameOfPerson",                    label: "Name of the person(s) to whom Renewed / Duplicate certificate is issued",    col: "(3)"  },
  { key: "approvalDate",                    label: "Date of approval of issue of Renewed / Duplicate share certificate",         col: "(4)"  },
  { key: "classOfShares",                   label: "Class of shares",                                                            col: "(5)"  },
  { key: "originalIssueDate",               label: "Date of issue of original share certificate",                                col: "(6)"  },
  { key: "originalShareCertificateNumber",  label: "Original share certificate number",                                          col: "(7)"  },
  { key: "totalSharesOriginalCertificate",  label: "Total number of shares in the Original Share Certificate",                   col: "(8)"  },
  { key: "distinctiveNumberRanges",         label: "Distinctive No. of shares (From – To)",                                      col: "(9)"  },
  { key: "renewedIssueDate",                label: "Date of issue of Renewed / Duplicate Share Certificate",                     col: "(10)" },
  { key: "reasonForIssue",                  label: "Reasons for issue of Renewed / Duplicate Share Certificate",                 col: "(11)" },
  { key: "renewedShareCertificateNumber",   label: "Number of the Renewed share certificate, if applicable",                    col: "(12)" },
  { key: "totalSharesRenewedCertificate",   label: "Total Number of Shares in the Renewed / Duplicate Share Certificate",       col: "(13)" },
  { key: "registerMembersReference",        label: "Reference to entry in Register of Members",                                  col: "(14)" },
  { key: "remarks",                         label: "Remarks",                                                                    col: "(15)" },
];

// Split into two parts for screen display
const PART_ONE = COLUMNS.slice(0, 9);           // cols (1)–(9)
const PART_TWO = [COLUMNS[0], ...COLUMNS.slice(9)]; // S.No repeated + cols (10)–(15)

const BLANK_ENTRY = Object.fromEntries(
  COLUMNS.filter(c => c.key !== "serialNo" && c.key !== "distinctiveNumberRanges")
    .map(c => [c.key, ""])
);
BLANK_ENTRY.distinctiveNumberRanges = [{ from: "", to: "" }];

const dateFields   = ["approvalDate", "originalIssueDate", "renewedIssueDate"];
const numberFields = ["totalSharesOriginalCertificate", "totalSharesRenewedCertificate"];

// ── Format cell values ────────────────────────────────────────────────────────
const fmt = (key, val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (key === "distinctiveNumberRanges" && Array.isArray(val)) {
    if (val.length === 0) return "—";
    return val.map(r => `${r.from} – ${r.to}`).join(", ");
  }
  return String(val);
};

// ── Sub-table component (screen) ──────────────────────────────────────────────
const SubTable = ({ columns, data, partLabel }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-md overflow-hidden">
    <div className="bg-slate-100 border-b border-slate-200 px-4 py-1.5">
      <span className="text-[11px] font-bold text-slate-600 uppercase tracking-widest">{partLabel}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[10px] border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            {columns.map(col => (
              <th key={col.key} className="border border-slate-600 px-2 py-1 text-center font-bold text-[9px] whitespace-nowrap">
                {col.col}
              </th>
            ))}
          </tr>
          <tr className="bg-slate-700 text-white">
            {columns.map(col => (
              <th
                key={col.key}
                className="border border-slate-600 px-2 py-2 text-center font-semibold text-[9px] leading-tight"
                style={{ minWidth: col.key === "serialNo" ? 36 : 100 }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
              {columns.map(col => (
                <td key={col.key} className="border border-slate-200 px-2 py-1.5 text-center text-[10px] align-middle">
                  {fmt(col.key, row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ── Add Entry Modal ───────────────────────────────────────────────────────────
const AddEntryModal = ({ onClose, onSave, saving }) => {
  const [form, setForm] = useState({ ...BLANK_ENTRY });

  const updateRange = (index, field, value) => {
    setForm(prev => {
      const ranges = [...prev.distinctiveNumberRanges];
      ranges[index] = { ...ranges[index], [field]: value };
      return { ...prev, distinctiveNumberRanges: ranges };
    });
  };

  const addRange = () => {
    setForm(prev => ({
      ...prev,
      distinctiveNumberRanges: [...prev.distinctiveNumberRanges, { from: "", to: "" }],
    }));
  };

  const removeRange = (index) => {
    setForm(prev => ({
      ...prev,
      distinctiveNumberRanges: prev.distinctiveNumberRanges.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="bg-primary text-white px-6 py-3 rounded-t-xl flex justify-between items-center">
          <h2 className="font-bold text-sm uppercase tracking-widest">Add New SH-2 Entry</h2>
          <button onClick={onClose} className="text-white hover:text-red-300 text-xl font-bold">✕</button>
        </div>

        <div className="overflow-y-auto p-4 flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {COLUMNS.filter(c => c.key !== "serialNo" && c.key !== "distinctiveNumberRanges").map(col => (
              <div key={col.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {col.col} {col.label}
                </label>
                <input
                  type={dateFields.includes(col.key) ? "date" : numberFields.includes(col.key) ? "number" : "text"}
                  value={form[col.key] || ""}
                  onChange={e => setForm(prev => ({ ...prev, [col.key]: e.target.value }))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            ))}
          </div>

          {/* Distinctive Number Ranges */}
          <div className="mt-4 p-3 border-2 border-slate-100 rounded-lg bg-slate-50">
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs font-bold text-slate-500 uppercase">(9) Distinctive No. of Shares</label>
              <button
                onClick={addRange}
                className="text-[10px] bg-blue-600 text-white px-2 py-1 rounded font-bold uppercase hover:bg-blue-700"
              >
                + Add Range
              </button>
            </div>
            {form.distinctiveNumberRanges.map((range, idx) => (
              <div key={idx} className="flex gap-2 items-center mb-2">
                <div className="flex-1">
                  <label className="block text-[10px] text-gray-500 mb-0.5">From</label>
                  <input
                    type="number"
                    value={range.from || ""}
                    onChange={e => updateRange(idx, "from", e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] text-gray-500 mb-0.5">To</label>
                  <input
                    type="number"
                    value={range.to || ""}
                    onChange={e => updateRange(idx, "to", e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                {form.distinctiveNumberRanges.length > 1 && (
                  <button
                    onClick={() => removeRange(idx)}
                    className="mt-4 text-red-500 hover:text-red-700 font-bold text-sm"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 font-semibold uppercase">
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className="px-4 py-2 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 font-semibold uppercase"
          >
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Print styles ──────────────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    @page { size: A3 landscape; margin: 8mm; }
    body * { visibility: hidden !important; }
    #sh2-pdf-area, #sh2-pdf-area * { visibility: visible !important; }
    #sh2-pdf-area { position: fixed; top: 0; left: 0; width: 100%; }
  }
`;

// ── Main SH-2 Viewer ──────────────────────────────────────────────────────────
const Sh2Viewer = ({ selectedClientData }) => {
  const token     = useSelector((state) => state.auth?.access_token);
  const accountId = selectedClientData?.accountId || selectedClientData?.id;

  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [showModal,     setShowModal]     = useState(false);
  const [error,         setError]         = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [status,        setStatus]        = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Inject print styles once
  useEffect(() => {
    const style = document.createElement("style");
    style.id    = "sh2-print-style";
    style.innerHTML = PRINT_STYLES;
    if (!document.getElementById("sh2-print-style")) document.head.appendChild(style);
    return () => document.getElementById("sh2-print-style")?.remove();
  }, []);

  useEffect(() => {
    if (accountId && token) fetchData();
  }, [accountId, token]);

  const fetchData = async () => {
    if (!accountId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await sh2RegisterApi.fetchSh2Register(accountId, token);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setData(list);
    } catch (err) {
      setError("No data available for SH-2");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async (form) => {
    setSaving(true);
    setStatus(null);
    try {
      const payload = {
        folioNo:                        form.folioNo || "",
        nameOfPerson:                   form.nameOfPerson || "",
        approvalDate:                   form.approvalDate || null,
        classOfShares:                  form.classOfShares || "",
        originalIssueDate:              form.originalIssueDate || null,
        originalShareCertificateNumber: form.originalShareCertificateNumber || "",
        totalSharesOriginalCertificate: form.totalSharesOriginalCertificate ? parseInt(form.totalSharesOriginalCertificate, 10) : null,
        distinctiveNumberRanges:        form.distinctiveNumberRanges
          .filter(r => r.from !== "" || r.to !== "")
          .map(r => ({ from: r.from ? parseInt(r.from, 10) : null, to: r.to ? parseInt(r.to, 10) : null })),
        renewedIssueDate:               form.renewedIssueDate || null,
        reasonForIssue:                 form.reasonForIssue || "",
        renewedShareCertificateNumber:  form.renewedShareCertificateNumber || "",
        totalSharesRenewedCertificate:  form.totalSharesRenewedCertificate ? parseInt(form.totalSharesRenewedCertificate, 10) : null,
        registerMembersReference:       form.registerMembersReference || "",
        remarks:                        form.remarks || "",
      };

      const res = await sh2RegisterApi.submitSh2Form(payload, accountId, token);
      setData(prev => [...prev, res]);
      setShowModal(false);
      setStatus({ type: "success", message: "Entry added successfully!" });
    } catch (err) {
      setStatus({ type: "error", message: err.message || "Failed to add entry." });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = () => {
    setGeneratingPdf(true);
    setTimeout(() => {
      window.print();
      setGeneratingPdf(false);
    }, 300);
  };

  // ── PDF sub-table (print area) ──────────────────────────────────────────────
  const PrintSubTable = ({ columns, partLabel }) => (
    <div style={{ marginBottom: 12, color: "#000" }}>
      <div style={{ fontWeight: "bold", fontSize: 12, color: "#000", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>
        {partLabel}
      </div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 7, color: "#000" }}>
        <thead>
          <tr style={{ background: "#1e3a5f" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #000", padding: "2px 3px", textAlign: "center", fontSize: 12, whiteSpace: "nowrap", color: "#000" }}>
                {col.col}
              </th>
            ))}
          </tr>
          <tr style={{ background: "#2d4f7c" }}>
            {columns.map(col => (
              <th key={col.key} style={{ border: "1px solid #000", padding: "3px 3px", textAlign: "center", fontSize: 12, lineHeight: 1.3, color: "#000", minWidth: col.key === "serialNo" ? 28 : 80 }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id || i} style={{ background: "#fff" }}>
              {columns.map(col => (
                <td key={col.key} style={{ border: "1px solid #000", padding: "2px 3px", textAlign: "center", fontSize: 12, verticalAlign: "middle", color: "#000" }}>
                  {fmt(col.key, row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div className="bg-white border-b-4 px-6 py-2 mb-2 text-center shadow-sm rounded-t-lg">
        <h1 className="text-2xl font-black uppercase tracking-tight">Form No. SH - 2</h1>
        <h2 className="text-lg font-bold mt-1">Register of Renewed and Duplicate Share Certificates</h2>
        <p className="text-[10px] italic text-slate-500 mt-1 max-w-4xl mx-auto">
          [Pursuant to sub-section (3) of section 46 of the Companies Act, 2013 and rule 6(3)(a)
          the Companies (Share Capital and Debentures) Rules 2014]
        </p>
      </div>

      {/* ── Controls ── */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-2 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => { setShowModal(true); setStatus(null); }}
            disabled={!accountId}
            className="bg-primary text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            + Add New Entry
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={data.length === 0 || generatingPdf}
            className="bg-green-600 text-white text-xs px-4 py-2 rounded-md font-bold uppercase shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* ── Status Banner ── */}
      {status && (
        <div className={`mb-2 px-4 py-3 rounded-lg text-sm font-medium border ${
          status.type === "success"
            ? "bg-green-50 text-green-800 border-green-200"
            : "bg-red-50 text-red-800 border-red-200"
        }`}>
          {status.message}
        </div>
      )}

      {/* ── Screen Tables ── */}
      {loading ? (
        <div className="text-center py-10 text-gray-500 text-sm">Loading...</div>
      ) : data.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-sm italic">
          {accountId ? "No entries found for this company." : "Select a company to view records."}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <SubTable
            columns={PART_ONE}
            data={data}
            partLabel="Part 1 — Columns (1) to (9)"
          />
          <SubTable
            columns={PART_TWO}
            data={data}
            partLabel="Part 2 — Columns (10) to (15)"
          />
          <div className="text-xs text-gray-500 text-right px-1">
            Total entries: <span className="font-bold text-gray-700">{data.length}</span>
          </div>
        </div>
      )}

      {/* ── Hidden PDF Print Area ── */}
      {data.length > 0 && (
        <div id="sh2-pdf-area" style={{ display: "none" }}>
          <style>{`@media print { #sh2-pdf-area { display: block !important; } }`}</style>
          <div style={{ textAlign: "center", marginBottom: 10, fontFamily: "Arial, sans-serif" }}>
            <h1 style={{ fontSize: 15, fontWeight: "bold", margin: 0 }}>Form No. SH - 2</h1>
            <h2 style={{ fontSize: 13, margin: "2px 0" }}>Register of Renewed and Duplicate Share Certificates</h2>
            <p style={{ fontSize: 10, margin: 0, color: "black", fontStyle: "italic" }}>
              [Pursuant to sub-section (3) of section 46 of the Companies Act, 2013 and rule 6(3)(a)
              the Companies (Share Capital and Debentures) Rules 2014]
            </p>
          </div>
          <PrintSubTable columns={PART_ONE} partLabel="Part 1 — Columns (1) to (9)" />
          <div style={{ height: 16 }} />
          <PrintSubTable columns={PART_TWO} partLabel="Part 2 — Columns (10) to (15)" />
        </div>
      )}

      {/* ── Add Entry Modal ── */}
      {showModal && (
        <AddEntryModal
          onClose={() => setShowModal(false)}
          onSave={handleSaveEntry}
          saving={saving}
        />
      )}
    </div>
  );
};

export default Sh2Viewer;